import { useState } from 'react';
import { Appointment, AppointmentStatus, PaginatedResponse, PaymentMethod } from '../types/entities';
import { AppointmentFormData } from '../types/forms';
import { getAuthenticatedFetch } from '../utils/api';
import { useErrorHandler } from '../utils/errorHandler';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface ApiError {
  message: string;
  status?: number;
}

interface AppointmentQueryParams {
  clientId?: number;
  hairdresserId?: number;
  status?: AppointmentStatus;
  date?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export const useAppointmentsApi = () => {
  const [error, setError] = useState<ApiError | null>(null);
  const { handleApiError: handleError } = useErrorHandler();

  const handleApiError = (err: any): ApiError => {
    const apiError = handleError(err);
    setError(apiError);
    return apiError;
  };

  const clearError = () => setError(null);

  const fetchAppointments = async (queryParams?: AppointmentQueryParams): Promise<PaginatedResponse<Appointment>> => {
    try {
      clearError();
      const queryString = queryParams ? new URLSearchParams(
        Object.entries(queryParams).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = value.toString();
          }
          return acc;
        }, {} as Record<string, string>)
      ).toString() : '';
      
      const url = queryString ? `/appointments?${queryString}` : `/appointments`;
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(url);
      
      const data: PaginatedResponse<Appointment> = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const fetchAppointment = async (id: number): Promise<Appointment> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/appointments/${id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const createAppointment = async (appointmentData: AppointmentFormData): Promise<Appointment> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/appointments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointmentData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const updateAppointment = async (id: number, appointmentData: Partial<AppointmentFormData>): Promise<Appointment> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/appointments/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointmentData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const changeAppointmentStatus = async (id: number, status: AppointmentStatus): Promise<Appointment> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/appointments/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const deleteAppointment = async (id: number): Promise<void> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/appointments/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const finalizeAppointment = async (id: number, payments: Array<{ method: PaymentMethod; amount: number }>, discount?: { type: 'percent' | 'fixed'; amount: number }, serviceDiscounts?: Array<{ serviceIndex: number; type: 'percent' | 'fixed'; amount: number }>): Promise<Appointment> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/appointments/${id}/finalize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ payments, discount, serviceDiscounts }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  return {
    error,
    clearError,
    fetchAppointments,
    fetchAppointment,
    createAppointment,
    updateAppointment,
    changeAppointmentStatus,
    deleteAppointment,
    finalizeAppointment,
  };
};

